import React, { useCallback, useMemo } from "react";

import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { BillingRunAction } from "actions";

import { AdminLink } from "components/AdminLink/AdminLink";
import AuditLogLink from "components/AuditLog/AuditLogLink";
import { BillingRunForm } from "components/Billing/BillingRun/Form";
import { DefaultInitialValues } from "components/Billing/BillingRun/lib";
import { getValidationSchema } from "components/Billing/BillingRun/validationSchema";
import { ConfirmDialog } from "components/ConfirmDialog";
import { FullWidthSlimErrorMessage } from "components/ErrorMessage";
import { Button, DeleteButton, SecondaryButton } from "components/Form";
import { useSubmitHandler } from "components/Form/FormikControls";
import { Tooltip } from "components/Form/FormikControls/Tooltip";
import { Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import { AuditLogTypes } from "constants/auditLog";
import { BillingRunPermissions } from "constants/billingRuns";
import { ApiModel } from "constants/loading";
import { ModalTypes } from "constants/navigation";

import { integratedBillingDocumentDescription } from "lib/billingDocument";
import { getLivestockSaleId } from "lib/navigation";
import { hasPermission } from "lib/permissions";

import {
  getBillingRunById,
  getBillingDocumentsWithActiveIntegrationByBillingRunId,
  getBillingRuns,
  getCurrentSale,
  getDefaultBillingSettings,
  getRuleBooks,
} from "selectors";

import {
  useBoolean,
  useCanAddBillingRunPermission,
  useHasBillingPermission,
  useModalAdapter,
} from "hooks";

export function BillingRunModalAdapter(props) {
  const [hashParams, returnTo, onClose] = useModalAdapter(
    ModalTypes.EditBillingRun,
    props,
  );

  const { billingRunId = null, ruleBookId = null } = hashParams || {};

  const hasBillingRunFeaturePermission = useHasBillingPermission();
  if (!hasBillingRunFeaturePermission) {
    return null;
  }

  return (
    <WaitForSync requiredData={[ApiModel.BILLING_RUNS]}>
      <BillingRunModal
        billingRunId={billingRunId}
        onClose={onClose}
        returnTo={returnTo}
        ruleBookId={ruleBookId}
      />
    </WaitForSync>
  );
}

export function Footer(props) {
  const { billingRunId, onClose, onDelete } = props;
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false);
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
    useBoolean();
  const billingRun = useSelector(getBillingRunById(billingRunId));
  const isEdit = Boolean(billingRun);

  const hasCreatePermission = useCanAddBillingRunPermission();

  const hasDeletePermission =
    isEdit && hasPermission(billingRun, BillingRunPermissions.delete);

  const billingDocumentsWithActiveIntegration =
    useSelector(
      getBillingDocumentsWithActiveIntegrationByBillingRunId(billingRunId),
    ) || [];

  const deleteDisabled = billingDocumentsWithActiveIntegration.length !== 0;

  const deleteToolTipComponent = (
    <>
      This billing run contains documents that have been exported to Xero. VOID
      or DELETE these in Xero before deleting this run:
      <ul>
        {billingDocumentsWithActiveIntegration.map(document => (
          <li key={document.id}>
            {integratedBillingDocumentDescription(document)}
          </li>
        ))}
      </ul>
    </>
  );

  const deleteTooltip = billingDocumentsWithActiveIntegration.length
    ? deleteToolTipComponent
    : "";

  const hasUpdatePermission =
    isEdit && hasPermission(billingRun, BillingRunPermissions.update);

  useSubmitHandler(false, setIsSubmitEnabled);

  return (
    <>
      <SecondaryButton type="button" onClick={onClose}>
        Close
      </SecondaryButton>
      {isEdit ? (
        <>
          {hasDeletePermission && (
            <Row flexGrow>
              <Tooltip title={deleteTooltip} placement="top" fullWidth>
                <DeleteButton
                  data-tour="delete"
                  type="button"
                  onClick={openConfirmDialog}
                  disabled={deleteDisabled}
                  fullWidth
                >
                  Delete
                </DeleteButton>
              </Tooltip>
            </Row>
          )}
          {hasUpdatePermission && (
            <Button data-tour="save" type="submit" disabled={!isSubmitEnabled}>
              Save
            </Button>
          )}
        </>
      ) : (
        <>
          {hasCreatePermission && (
            <Button
              data-tour="create"
              type="submit"
              disabled={!isSubmitEnabled}
            >
              Create
            </Button>
          )}
        </>
      )}

      <ConfirmDialog
        title="Confirm Delete Billing Run"
        isOpen={isConfirmDialogOpen}
        onCancel={closeConfirmDialog}
        onDelete={onDelete}
      />
    </>
  );
}

export function BillingRunModal(props) {
  const { billingRunId, onClose } = props;
  const dispatch = useDispatch();
  const billingRun = useSelector(getBillingRunById(billingRunId));
  const billingSettings = useSelector(getDefaultBillingSettings);
  const billingSettingsId = billingSettings ? billingSettings.id : null;

  const isEdit = Boolean(billingRun);

  const allBillingRuns = useSelector(getBillingRuns);
  const showMultipleRunsWarning =
    !isEdit && Object.values(allBillingRuns).length >= 1;

  const currentSale = useSelector(getCurrentSale);
  const ruleBooksById = useSelector(getRuleBooks);
  const validationSchema = useMemo(
    () =>
      getValidationSchema({
        ruleBooksById,
        saleSubTypeId: currentSale?.sale_sub_type_id,
        saleType: currentSale?.sale_type,
      }),
    [ruleBooksById, currentSale?.sale_sub_type_id, currentSale?.sale_type],
  );

  const initialValues = useMemo(
    () => ({
      // Apply defaults,
      ...DefaultInitialValues,
      id: uuidv4(),
      billingSettingsId,
      // TODO MultiSale Billing Runs will need to figure this out
      livestockSaleIds: [getLivestockSaleId()],
      // Apply values from the existing Billing Run
      ...billingRun,
      // Apply information passing in by args
    }),
    [billingRun, billingSettingsId],
  );

  const onClickDelete = useCallback(() => {
    dispatch(BillingRunAction.delete(billingRunId));
    onClose();
  }, [dispatch, billingRunId, onClose]);

  function onSubmit(values) {
    if (isEdit) {
      // Rule book is optional for non-Clearing Sales. Convert empty values to undefined.
      const valuesToSubmit = {
        ...values,
        ruleBookId: values.ruleBookId || undefined,
      };
      dispatch(
        BillingRunAction.update(valuesToSubmit, {
          changeReason: "Updated from Edit Billing Run modal",
        }),
      );
    } else {
      dispatch(BillingRunAction.create(values));
    }
    onClose();
  }

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle onClose={onClose}>
        {billingRunId && (
          <AuditLogLink
            auditLogType={AuditLogTypes.BILLING_RUN}
            dataId={billingRunId}
            returnTo={window.location.hash}
          />
        )}
        {isEdit ? "Edit" : "Create"} Billing Run
        <AdminLink model="billingrun" cbid={billingRunId} />
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <Form data-tour="billing-run-form">
          <DialogContent dividers>
            {showMultipleRunsWarning && (
              <Tooltip
                title="Creating multiple Billing Runs in a Sale can create export and data issues - only perform this action if you are certain you need to."
                placement="top"
                fullWidth
                iconPadding={0}
              >
                <FullWidthSlimErrorMessage>
                  There is already a Billing Run in this Sale.
                </FullWidthSlimErrorMessage>
              </Tooltip>
            )}
            <BillingRunForm billingRunId={billingRunId} />
          </DialogContent>
          <DialogActions>
            <Footer
              billingRunId={billingRunId}
              onDelete={onClickDelete}
              onClose={onClose}
            />
          </DialogActions>
        </Form>
      </Formik>
    </ZoomyDialog>
  );
}
